/*
 * Product image lightbox (item 4).
 *
 * Pairs with the `<mh-zoom>` markup in sections/mh-product-hero.liquid. Native `<dialog>` opened
 * with showModal(), which is the pattern the Horizon baseline uses for its own modals and the one
 * this build already uses for the stories video (D51): the focus trap, Esc and the ::backdrop come
 * from the platform, so what is left here is the viewer.
 *
 * Zoom behaviour:
 *   - Desktop: click the image to toggle 2x, centred on the point clicked, then pan by moving the
 *     pointer. Click again to zoom back out.
 *   - Mobile: the stage is a scroll container with `touch-action: pinch-zoom`, so the browser's own
 *     pinch and pan work, and a double tap toggles the same 2x.
 *
 * The image element is created on open and removed on close, so a 2400px file is never fetched for
 * a lightbox nobody opened, and closing releases it.
 */
/*
 * Wrapped in an IIFE with a load guard, so the file is safe to include more than once.
 *
 * `class MhZoom` at the top level of a classic script is a lexical declaration: a second copy of
 * this tag on the same page re-declares it and throws `SyntaxError: Identifier 'MhZoom' has already
 * been declared` before a line of it runs. The product page includes it once and never noticed. The
 * Neelofa landing template includes it from every section that owns a content image, because no
 * section there is guaranteed to be present, so it needs to survive being loaded four times.
 *
 * Same shape as `assets/mh-floating-bar.js` and `assets/mh-stories-modal.js`, which guard the same
 * way. Behaviour is unchanged: the first copy defines the element, the rest return immediately.
 */
(function () {
  if (window.mhZoomLoaded) return;
  window.mhZoomLoaded = true;

  class MhZoom extends HTMLElement {
    connectedCallback() {
      // Only the first instance in the document binds.
      //
      // The trigger listener is delegated on `document`, so a second instance would answer the very
      // same click and open a second dialog on top of the first. The product page renders exactly one
      // and is unaffected: first instance there is the only instance. The Neelofa landing template
      // renders one per section that owns a content image, because no section on it is guaranteed to
      // be present, and this is what makes those copies harmless. See docs/DECISIONS.md D64.
      if (document.querySelector('mh-zoom') !== this) return;

      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('[data-mh-zoom-stage]');
      this.closeBtn = this.querySelector('[data-mh-zoom-close]');
      if (!this.dialog || !this.stage || typeof this.dialog.showModal !== 'function') return;

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onClose = this.onClose.bind(this);

      // Delegated, so it keeps working for every slide without one listener per image.
      document.addEventListener('click', this.onTriggerClick);
      this.closeBtn?.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (e) => {
        // A click on the dialog itself is a click on the backdrop area around the image.
        if (e.target === this.dialog || e.target === this.stage) this.dialog.close();
      });
      this.dialog.addEventListener('close', this.onClose);

      // FIX 2 controls. The arrows and the counter are optional markup: a caller that renders the
      // older dialog without them still gets a working single-image lightbox.
      this.prevBtn = this.querySelector('[data-mh-zoom-prev]');
      this.nextBtn = this.querySelector('[data-mh-zoom-next]');
      this.counter = this.querySelector('[data-mh-zoom-counter]');
      this.prevBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.step(-1); });
      this.nextBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.step(1); });

      // Keyboard: the dialog has focus while open, so this never fires against the page behind it.
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      });

      /*
       * Swipe. Tracked on the stage with pointer events so one path covers touch and mouse drag.
       * A swipe is only a swipe when it is mostly horizontal and long enough, so a vertical scroll
       * of a zoomed image is never mistaken for one; and never while zoomed in, where a drag is a
       * pan. `touch-action: pinch-zoom` on the stage already gives the browser the pinch gesture,
       * and horizontal panning is not part of that, so nothing is being fought over here.
       */
      let sx = 0, sy = 0, tracking = false;
      this.stage.addEventListener('pointerdown', (e) => {
        if (this.zoomed) return;
        tracking = true; sx = e.clientX; sy = e.clientY;
      });
      this.stage.addEventListener('pointerup', (e) => {
        if (!tracking) return;
        tracking = false;
        const dx = e.clientX - sx;
        const dy = e.clientY - sy;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) this.step(dx < 0 ? 1 : -1);
      });
      this.stage.addEventListener('pointercancel', () => { tracking = false; });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTriggerClick);
    }

    onTriggerClick(event) {
      const trigger = event.target.closest('[data-mh-zoom]');
      if (!trigger) return;
      event.preventDefault();
      this.openFrom(trigger);
    }

    /*
     * FIX 2: the lightbox is a gallery, not a single picture.
     *
     * The set is every trigger inside the same `[data-mh-zoom-group]`. The product gallery marks
     * its root with that attribute, so opening any slide gives you all of them; a standalone
     * trigger (the Neelofa content images) has no group, so it is a set of one and the arrows stay
     * hidden. That keeps unrelated pictures on a landing page from being swiped together.
     */
    openFrom(trigger) {
      const group = trigger.closest('[data-mh-zoom-group]');
      this.items = group
        ? [...group.querySelectorAll('[data-mh-zoom]')]
        : [trigger];
      this.index = Math.max(0, this.items.indexOf(trigger));
      this.trigger = trigger;

      this.dialog.classList.toggle('is-gallery', this.items.length > 1);
      this.prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.show(this.index);
      this.closeBtn?.focus();
    }

    /** Move by n, wrapping, so a swipe past the last image returns to the first. */
    step(n) {
      if (!this.items || this.items.length < 2) return;
      this.index = (this.index + n + this.items.length) % this.items.length;
      this.show(this.index);
    }

    show(i) {
      const t = this.items[i];
      if (!t) return;
      this.trigger = t;
      this.zoomed = false;

      const img = document.createElement('img');
      img.className = 'mh-zoom__img';
      img.alt = t.dataset.mhZoomAlt || '';
      img.addEventListener('click', (e) => this.toggleZoom(e));
      img.addEventListener('dblclick', (e) => this.toggleZoom(e));
      img.addEventListener('pointermove', (e) => this.pan(e));
      img.src = t.dataset.mhZoom;

      this.stage.replaceChildren(img);
      this.img = img;
      if (this.counter) {
        this.counter.textContent = this.items.length > 1 ? `${i + 1} / ${this.items.length}` : '';
      }

      // FIX 3: trim the photograph's own white margin once it has decoded.
      this.trimWhite(img);
    }

    /*
     * FIX 3: the white "border" is inside the picture, so CSS could never remove it.
     *
     * Checked rather than assumed: the product shots are square files whose every edge pixel is
     * opaque 255,255,255. There is no border, background, padding or wrapper drawing it, which is
     * why rounding the corners last time did not help. It is the studio backdrop, baked in.
     *
     * Re-cutting the files is out of scope (the owner is re-uploading images in bulk separately),
     * so the trim happens at display time: the decoded image is measured on a canvas, the bounding
     * box of everything that is not near-white is found, and the image is scaled and offset so that
     * box fills the frame. The result is the product filling the lightbox with the rounded corner
     * against it, and no white margin.
     *
     * Every failure path leaves the picture exactly as it was: a cross origin file that taints the
     * canvas, a photograph with no white to trim, or one that is almost entirely white (a blank or
     * a mostly-white graphic, where a trim would be a wild crop) all fall through untouched.
     */
    trimWhite(img) {
      const apply = () => {
        if (img !== this.img) return; // a later image won the race
        let box;
        try {
          box = this.whiteTrimBox(img);
        } catch (e) {
          return; // tainted canvas, or no 2d context
        }
        if (!box) return;

        const { left, top, width, height } = box;
        // Ignore a trim that would barely change anything, and one so aggressive it suggests the
        // scan found the wrong thing.
        const area = (width * height) / (img.naturalWidth * img.naturalHeight);
        if (area > 0.92 || area < 0.05) return;

        /*
         * Redrawn rather than scaled.
         *
         * Scaling the image inside its square frame was the first attempt and it only half worked:
         * the frame stays square, so once the product's width fitted, its shorter dimension still
         * left the photograph's white showing as bands top and bottom. Copying just the trimmed
         * rectangle into a canvas gives an element whose intrinsic size IS the product, so there is
         * no leftover frame for white to sit in at any aspect ratio.
         *
         * The canvas carries the same class as the image, so the radius, the sizing and the zoom
         * transform all apply to it unchanged, and `role="img"` with the alt text keeps it
         * announced exactly as the image was.
         */
        const c = document.createElement('canvas');
        c.width = Math.round(width);
        c.height = Math.round(height);
        const ctx = c.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, left, top, width, height, 0, 0, c.width, c.height);

        c.className = img.className + ' is-trimmed';
        c.setAttribute('role', 'img');
        c.setAttribute('aria-label', img.alt || '');
        c.addEventListener('click', (e) => this.toggleZoom(e));
        c.addEventListener('dblclick', (e) => this.toggleZoom(e));
        c.addEventListener('pointermove', (e) => this.pan(e));

        this.stage.replaceChildren(c);
        this.img = c;
        this.source = img; // kept so zooming can go back to the untrimmed file
      };

      if (img.complete && img.naturalWidth) apply();
      else img.addEventListener('load', apply, { once: true });
    }

    /** The bounding box of everything that is not near-white, or null if there is nothing to trim. */
    whiteTrimBox(img) {
      const W = img.naturalWidth;
      const H = img.naturalHeight;
      if (!W || !H) return null;

      // Sampled small: a 200px scan is plenty to find a studio margin and costs no time.
      const s = 200 / Math.max(W, H);
      const w = Math.max(1, Math.round(W * s));
      const h = Math.max(1, Math.round(H * s));
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, w, h);
      const d = ctx.getImageData(0, 0, w, h).data;

      const NEAR_WHITE = 246; // JPEG ringing means a studio white is rarely a clean 255
      let x0 = w, y0 = h, x1 = -1, y1 = -1;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          const isWhite = d[i] >= NEAR_WHITE && d[i + 1] >= NEAR_WHITE && d[i + 2] >= NEAR_WHITE;
          const isClear = d[i + 3] < 8;
          if (isWhite || isClear) continue;
          if (x < x0) x0 = x;
          if (y < y0) y0 = y;
          if (x > x1) x1 = x;
          if (y > y1) y1 = y;
        }
      }
      if (x1 < 0) return null; // nothing but white

      /*
       * A hair of bleed so the trim never shaves the product's own edge. Kept small: at 1.5% it was
       * leaving a visible white rim, which is the whole thing this is here to remove.
       *
       * What cannot be removed this way is white that falls INSIDE the product's bounding box, for
       * example either side of the box's carry handle. That is part of the photograph, and short of
       * re-cutting the source files, which is the owner's separate image project, it stays.
       */
      const pad = Math.round(Math.max(w, h) * 0.004);
      x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad);
      x1 = Math.min(w - 1, x1 + pad); y1 = Math.min(h - 1, y1 + pad);
      return {
        left: (x0 / w) * W,
        top: (y0 / h) * H,
        width: ((x1 - x0 + 1) / w) * W,
        height: ((y1 - y0 + 1) / h) * H,
      };
    }

    /** Toggle 2x, anchored on the point the customer actually pointed at. */
    toggleZoom(event) {
      if (!this.img) return;
      event.stopPropagation();
      this.zoomed = !this.zoomed;
      this.img.classList.toggle('is-zoomed', this.zoomed);

      /*
       * The trim is a redraw rather than a transform, so zoom composes with it cleanly: 2x of the
       * trimmed canvas is 2x of the product, which is what someone inspecting detail wants.
       */
      if (this.zoomed) {
        this.setOrigin(event);
      } else {
        this.img.style.transformOrigin = '';
      }
    }

    pan(event) {
      if (this.zoomed && event.pointerType === 'mouse') this.setOrigin(event);
    }

    setOrigin(event) {
      const r = this.img.getBoundingClientRect();
      const x = ((event.clientX - r.left) / r.width) * 100;
      const y = ((event.clientY - r.top) / r.height) * 100;
      this.img.style.transformOrigin = `${Math.min(Math.max(x, 0), 100)}% ${Math.min(Math.max(y, 0), 100)}%`;
    }

    onClose() {
      this.stage.replaceChildren();
      this.img = null;
      this.source = null;
      this.items = null;
      this.dialog.classList.remove('is-gallery');
      document.body.style.overflow = this.prevOverflow || '';
      if (this.trigger) {
        this.trigger.focus();
        this.trigger = null;
      }
    }
  }

  if (!customElements.get('mh-zoom')) {
    customElements.define('mh-zoom', MhZoom);
  }
})();
